import { defineStore } from "pinia"

export const useNotificationsStore = defineStore("notifications", {
  state: () => ({
    notifications: [],
    unreadCount: 0,
    preferences: null,
    isLoading: false,
    error: null,
    // System Banners State
    systemBanners: [],
    dismissedBanners: [],
  }),

  getters: {
    unreadNotifications: (state) => state.notifications.filter((n) => !n.read_at),

    recentNotifications: (state) => {
      return state.notifications.slice(0, 10)
    },

    notificationsByType: (state) => (type) => {
      return state.notifications.filter((n) => n.type === type)
    },

    hasUnread: (state) => state.unreadCount > 0,

    // System Banners Getters
    visibleBanners: (state) => {
      return state.systemBanners.filter((banner) => !state.dismissedBanners.includes(banner.id))
    },
  },

  actions: {
    /**
     * Fetch notifications from API
     */
    async fetchNotifications(filters = {}) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const authStore = useAuthStore()

        const params = {
          user_id: authStore.user?.id,
          limit: filters.limit || 50,
          offset: filters.offset || 0,
          unread_only: filters.unreadOnly || false,
        }

        const response = await this.$axios.get("/api/v1/notifications", { params })

        this.notifications = response.data.data
        this.unreadCount = this.notifications.filter((n) => !n.read_at).length

        return { success: true, data: response.data.data, meta: response.data.meta }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch notifications"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Get unread count
     */
    async fetchUnreadCount() {
      try {
        // Using this.$axios injected by pinia plugin
        const authStore = useAuthStore()

        const response = await this.$axios.get("/api/v1/notifications/unread-count", {
          params: { user_id: authStore.user?.id },
        })

        this.unreadCount = response.data.data.count

        return { success: true, count: response.data.data.count }
      } catch (error) {
        return { success: false, error: error.response?.data?.message }
      }
    },

    /**
     * Mark notification as read
     */
    async markAsRead(notificationId) {
      try {
        // Using this.$axios injected by pinia plugin

        await this.$axios.post(`/api/v1/notifications/${notificationId}/read`)

        const notification = this.notifications.find((n) => n.id === notificationId)
        if (notification && !notification.read_at) {
          notification.read_at = new Date().toISOString()
          notification.is_read = true
          this.unreadCount = Math.max(0, this.unreadCount - 1)
        }

        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to mark notification as read"
        return { success: false, error: this.error }
      }
    },

    /**
     * Mark all as read
     */
    async markAllAsRead() {
      try {
        // Using this.$axios injected by pinia plugin
        const authStore = useAuthStore()

        await this.$axios.post("/api/v1/notifications/read-all", {
          user_id: authStore.user?.id,
        })

        const now = new Date().toISOString()
        this.notifications.forEach((notification) => {
          if (!notification.read_at) {
            notification.read_at = now
            notification.is_read = true
          }
        })
        this.unreadCount = 0

        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to mark all as read"
        return { success: false, error: this.error }
      }
    },

    /**
     * Delete notification
     */
    async deleteNotification(notificationId) {
      try {
        // Using this.$axios injected by pinia plugin

        await this.$axios.delete(`/api/v1/notifications/${notificationId}`)

        const index = this.notifications.findIndex((n) => n.id === notificationId)
        if (index !== -1) {
          if (!this.notifications[index].read_at) {
            this.unreadCount = Math.max(0, this.unreadCount - 1)
          }
          this.notifications.splice(index, 1)
        }

        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to delete notification"
        return { success: false, error: this.error }
      }
    },

    /**
     * Clear all read notifications
     */
    async clearReadNotifications() {
      try {
        // Using this.$axios injected by pinia plugin
        const authStore = useAuthStore()

        await this.$axios.delete("/api/v1/notifications/clear-read", {
          data: { user_id: authStore.user?.id },
        })

        this.notifications = this.notifications.filter((n) => !n.read_at)

        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to clear notifications"
        return { success: false, error: this.error }
      }
    },

    /**
     * Fetch notification preferences from API
     */
    async fetchPreferences() {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const authStore = useAuthStore()

        const response = await this.$axios.get(`/api/v1/notifications/preferences/${authStore.user?.id}`)

        this.preferences = response.data.data.preferences

        return { success: true, data: this.preferences }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch preferences"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Update notification preferences via API
     */
    async updatePreferences(updates) {
      this.isLoading = true
      this.error = null

      try {
        // Using this.$axios injected by pinia plugin
        const authStore = useAuthStore()

        const response = await this.$axios.put(`/api/v1/notifications/preferences/${authStore.user?.id}`, {
          preferences: updates,
        })

        this.preferences = response.data.data.preferences

        return { success: true, data: this.preferences }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to update preferences"
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Add notification (for real-time updates)
     */
    addNotification(notification) {
      this.notifications.unshift(notification)
      if (!notification.read_at) {
        this.unreadCount++
      }
    },

    /**
     * Add system banner (for real-time updates)
     */
    addSystemBanner(banner) {
      // Check if banner already exists
      const existingIndex = this.systemBanners.findIndex((b) => b.id === banner.id)
      if (existingIndex === -1) {
        this.systemBanners.unshift(banner)
      }
    },

    /**
     * Update system banner (for real-time updates)
     */
    updateSystemBanner(banner) {
      const index = this.systemBanners.findIndex((b) => b.id === banner.id)
      if (index !== -1) {
        this.systemBanners[index] = banner
      } else {
        // If not found, add it
        this.systemBanners.unshift(banner)
      }
    },

    /**
     * Remove system banner (for real-time updates)
     */
    removeSystemBanner(bannerId) {
      const index = this.systemBanners.findIndex((b) => b.id === bannerId)
      if (index !== -1) {
        this.systemBanners.splice(index, 1)
      }
    },

    /**
     * Clear error
     */
    clearError() {
      this.error = null
    },

    /**
     * Reset store
     */
    reset() {
      this.notifications = []
      this.unreadCount = 0
      this.preferences = null
      this.isLoading = false
      this.error = null
      this.systemBanners = []
      this.dismissedBanners = []
    },

    /**
     * Fetch active system banners
     */
    async fetchSystemBanners(userRole = "shipper") {
      try {
        const response = await this.$axios.get(`/api/v1/notifications/system-banners/active?user_role=${userRole}`)
        this.systemBanners = response.data.data || []

        // Track views for each banner
        this.systemBanners.forEach((banner) => {
          this.trackBannerView(banner.id)
        })

        return { success: true, data: this.systemBanners }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to fetch system banners"
        return { success: false, error: this.error }
      }
    },

    /**
     * Dismiss a system banner
     */
    async dismissSystemBanner(bannerId) {
      // Add to dismissed list
      if (!this.dismissedBanners.includes(bannerId)) {
        this.dismissedBanners.push(bannerId)
      }

      // Track dismiss via API
      try {
        await this.$axios.post(`/api/v1/notifications/system-banners/${bannerId}/dismiss`)
        return { success: true }
      } catch (error) {
        this.error = error.response?.data?.message || "Failed to dismiss banner"
        return { success: false, error: this.error }
      }
    },

    /**
     * Track banner view
     */
    async trackBannerView(bannerId) {
      try {
        await this.$axios.post(`/api/v1/notifications/system-banners/${bannerId}/view`)
        return { success: true }
      } catch (error) {
        // Silently fail for tracking
        console.error("Failed to track banner view:", error)
        return { success: false, error: error.response?.data?.message }
      }
    },

    /**
     * Load dismissed banners from persisted state (if needed for hydration)
     */
    loadDismissedBanners(dismissedIds = []) {
      this.dismissedBanners = dismissedIds
    },
  },
  persist: true,
})
